
export interface IPreview {
  title: string;
  subtitle: string;
  content: string[];
  imageUrl: string;
}


export interface IPlatform {
  title: string;
  imageUrl: string;
  content: string[];
}


export interface IFAQItem {
  question: string;
  answers: string[];
}

export interface IFAQCard {
  title: string;
  items: IFAQItem[];
  hoverColor: string;
}


export interface IServiceCard {
  title: string;
  iconUrl: string;
  content: string[];
}


export interface ITechnology {
  name: string;
  width: number;
  height: number;
  logo: string;
}

export interface ITechnologyCategory {
  category: string;
  items: ITechnology[];
}

export interface IKeyFeatureCard {
  title: string;
  text: string | string[];
  image: string;
}

export interface IKeyFeatures {
  title: string;
  cards: IKeyFeatureCard[];
}

export interface IStatisticsTeam {
  title: string;
  points: string[];
}

export interface IStatisticsPoint {
  mainPoint: string;
  secondPoint: string;
}

export interface IStatistics {
  title: string;
  imageUrl: string;
  teams: IStatisticsTeam[];
  points: IStatisticsPoint[];
}

export interface IFeedback {
  title: string;
  quoteSrc: string;
  comment: string;
  authorName: string;
  authorAvatar: string;
  authorSpecialist: string;
}

export interface IWebPlatformState {
  preview: IPreview;
  platform: IPlatform;
  faq: IFAQCard;
  services: IServiceCard;
  keyFeatures: IKeyFeatures;
  feedback: IFeedback;
  statistics: IStatistics;
  technologyStack: ITechnologyCategory[];
}

const imgBasePath = "/web-neobanking";
const iconsBasePath = `${imgBasePath}/icons`;

export const IWebPlatformState : IWebPlatformState = {
  preview: {
    title: "Web.3 Neobanking Platform",
    subtitle: "An efficient crypto and fiat neobanking solution",
    content: [
      "Welcome to Web.3 Neobanking Platform, an international digital bank that provides various financial services and products for B2C and B2B markets that emphasizes security and transparency. It strives to develop a digital economy by combining fiat money and cryptocurrencies and promoting new types of monetary policies.",
      "Web.3 Neobanking Platform’s virtual IBAN accounts in Euro and Pound enable seamless cross-border transactions and offer a multi-currency wallet that supports various cryptocurrencies and traditional fiat currencies.",
    ],
    imageUrl: `${imgBasePath}/neobanking-prewview.png`,
  },
  platform: {
    title: "Neobanking platform’s opportunities",
    imageUrl: `${imgBasePath}/platform-oportunites.png`,
    content: [
      "A debit card system",
      "Crypto-backed loans",
      "A user-friendly crypto-swapping feature",
      "Auto conversion",
      "A fiat-to-crypto and crypto-to-fiat exchange service",
    ],
  },
  faq: {
    title: "Development challenges to overcome",
    items: [
      {
        question: "Maximizing security of cryptographic key management",
        answers: [
          "To resolve this challenge, we have designed a mechanism for creating and storing keys in a password-protected database.",
          "The keys are never pulled into the application's memory except when a transaction must be signed. The user enters the password; the key is extracted, decrypted, used to sign the transaction, and immediately deleted from memory. Using adequate encryption algorithms gives an enhanced level of certainty against discovered attacks.",
        ],
      },
      {
        question: "Ensuring the safety of key storage",
        answers: [
          "Our team outsourced key storage to third-party organizations such as BitGo and Fireblocks to implement Custodial solutions. These companies have the right permits and can ensure optimum safeguard of important storage.",
          "Since in Custodial solutions, cryptographic keys are not stored on our servers, ensuring a seamless and secure transaction process on the blockchain through trusted partners was critical.",
          "Networking with these organizations is done through secure protocols such as Hypertext Transfer Protocol Secure (HTTPS) so as to ensure that the information exchange is secure. All the requests are signed with keys to ensure that during further work on the data, they remain not only non-interchangeable but also unaltered.",
          "One more advantage is that by working this way, we were able to concentrate on the creation of the Web.3 Neobanking Platform essential functionality, at the same time shifting essential critical aspects related to interaction with blockchains to specialized organizations. It contributes to increasing the reliability of the system and brings an extra layer of confidence to our users, as they will be fully aware that their transactions are secured at the highest level possible.",
          "Thus, it is still more secure and efficient to interact with blockchains through third-party services, which saves resources and efforts to work on the product while constantly enhancing the user experience.",
        ],
      },
      {
        question: "Granting constant access to current market information",
        answers: [
          "Ensuring constant access to up-to-date market information is critical, especially regarding data accuracy. For example, if the Web.3 Neobanking Platform uses the Kraken deal to exchange certain currencies; getting the exact exchange rate on Kraken for each transaction is essential.",
          "Our market service has been designed to regularly collect and cache information from precisely suitable sources, such as Kraken, and provide it on demand to the Web.3 Neobanking Platform applications, providing clients with accurate data for financial decision-making. We have created a market service that aggregates, caches, and updates data from various sources such as CoinGecko, Kraken, etc",
          "This made it possible to avoid frequent and expensive requests to third-party providers. The service flexibly configures requests: it can quickly provide a general rate or accurate data from a specific provider, such as Kraken. This service has become a central hub for obtaining market information for all Web.3 Neobanking Platform’s applications, significantly increasing efficiency and reducing integration costs with various APIs.",
        ],
      },
    ],
    hoverColor: "gray",
  },
  services: {
    title: "How it all began",
    iconUrl: `${imgBasePath}/how-it-all-began.png`,
    content: [
      "Our cooperation started with the Client's request to develop a cryptocurrency app and a fully functional website. The founder of Web.3 Neobanking Platform aimed to make the existence of both traditional and cryptocurrencies an investment and usage possibility for everyone.",
      "In 2021, the Web.3 Neobanking Platform began envisioning the broader future of banking. So, the Agilie expert team developed a Neobanking platform that simplifies depositing, making payments, and saving in both fiat and cryptocurrencies, facilitating seamless transactions and spending of cryptocurrencies.",
      "Our goals were to create the highest level of customer support service and integration issues with fiat and cryptocurrency accounts through API.",
      "Web.3 Neobanking Platform transformed into a tool for transactions, savings, and deposits and a digital banking solution for individuals and business entities in Canada, the USA, the UK, Australia, and some parts of Europe.",
    ],
  },
  technologyStack: [
    {
      category: "Cloud solutions",
      items: [
        {
          name: "Firebase",
          logo: `${iconsBasePath}/firebase.png`,
          width: 30,
          height: 40,
        },
        {
          name: "FCM",
          logo: `${iconsBasePath}/fcm.png`,
          width: 35,
          height: 40,
        },
        {
          name: "Firebase Analytics",
          logo: `${iconsBasePath}/firebase-analytics.png`,
          width: 36,
          height: 40,
        },
      ],
    },
    {
      category: "Databases",
      items: [
        {
          name: "PostgreSQL",
          logo: `${iconsBasePath}/postreSql.png`,
          width: 40,
          height: 40,
        },
        {
          name: "Prisma",
          logo: `${iconsBasePath}/prisma.png`,
          width: 33,
          height: 40,
        },
        {
          name: "Redis",
          logo: `${iconsBasePath}/redis.png`,
          width: 40,
          height: 40,
        },
      ],
    },
    {
      category: "Backend development",
      items: [
        {
          name: "Node.js",
          logo: `${iconsBasePath}/node-js.png`,
          width: 35,
          height: 40,
        },
        {
          name: "NestJS",
          logo: `${iconsBasePath}/nestjs.png`,
          width: 40,
          height: 40,
        },
        {
          name: "TypeScript",
          logo: `${iconsBasePath}/typescript.png`,
          width: 40,
          height: 40,
        },
      ],
    },
    {
      category: "Frontend development",
      items: [
        {
          name: "Angular",
          logo: `${iconsBasePath}/angular.png`,
          width: 38,
          height: 40,
        },
        {
          name: "React",
          logo: `${iconsBasePath}/react.png`,
          width: 38,
          height: 40,
        },
        {
          name: "PrimeNG",
          logo: `${iconsBasePath}/primeng.png`,
          width: 38,
          height: 40,
        },
      ],
    },
    {
      category: "Mobile development",
      items: [
        {
          name: "NativeScript",
          logo: `${iconsBasePath}/native-script.png`,
          width: 40,
          height: 40,
        },
        {
          name: "Swift",
          logo: `${iconsBasePath}/swift.png`,
          width: 40,
          height: 40,
        },
        {
          name: "Kotlin",
          logo: `${iconsBasePath}/kotlin.png`,
          width: 40,
          height: 40,
        },
        {
          name: "Compose",
          logo: `${iconsBasePath}/compose.png`,
          width: 36,
          height: 40,
        },
      ],
    },
  ],

  keyFeatures: {
    title: "Key features",
    cards: [
      {
        title: "Virtual IBAN",
        text: "The Web.3 Neobanking Platform provides virtual IBANs in Euro and Pound, allowing customers to accept payments and perform cross-border transactions smoothly.",
        image: `${imgBasePath}/virtual-iban.png`,
      },
      {
        title: "Multi-currency crypto andfiat account",
        text: "The Web.3 Neobanking Platform is a multicurrency wallet that can accommodate different cryptocurrencies as well as traditional money. Concisely, it provides users with a safe and efficient hub for handling all their digital assets.",
        image: `${imgBasePath}/multi-currency.png`,
      },
      {
        title: "Flexible gift certificates",
        text: [
          "This is a very special app feature. It provides a great opportunity to please your loved ones not only with a greeting card but with a gift certificate from a popular store or service attached to it. ",
          "Givingli works with wide-known brands such as Adidas, Airbnb, Uber, Sephora, iTunes, and more than 50 other no less illustrious names. An important point: it's up to you to decide how much money to put on a gift card, there are no fixed sums.",
        ],
        image: `${imgBasePath}/multi-currency.png`,
      },
      {
        title: "Fiat-crypto exchanges",
        text: [
          "The platform offers a secure and reliable exchange service for converting traditional fiat currencies and cryptocurrencies (fiat-to-crypto and crypto-to-fiat), competitive rates, and a seamless experience for both investment and everyday expenses.",
        ],
        image: `${imgBasePath}/fiat-crypto.png`,
      },
      {
        title: "Transaction details",
        text: [
          "The application guarantees the best user experience, so any cryptocurrency or fiat transaction is simple, clear, and transparent. Users can access the details of their transactions and see their status, among other things. This feature also allows them to understand which have been successful and which have failed.",
        ],
        image: `${imgBasePath}/transaction-details.png`,
      },
      {
        title: "Exchange, send andreceive crypto",
        text: [
          "The platform supports all popular cryptocurrencies. You can freely exchange your funds and move them from one e-wallet to another, depending on the type of cryptocurrency. Sending and receiving your funds to other users takes a minimum of time. Everything is thought out to the smallest detail to make the user experience easy, fast, safe, and clear.",
        ],
        image: `${imgBasePath}/exchange.png`,
      },
      {
        title: "Crypto payments",
        text: [
          "The Web.3 Neobanking Platform allows users to pay and receive cryptocurrencies directly from the app, utilizing decentralized technology for fast, secure transactions. The platform offers payment features, QR code scanning, and transaction tracking.",
        ],
        image: `${imgBasePath}/crypto-payments.png`,
      },
      {
        title: "Crypto savings",
        text: [
          "The platform allows customers to store funds and receive interest on their cryptocurrencies with fixed-term rates.",
        ],
        image: `${imgBasePath}/crypto-savings.png`,
      },
      {
        title: "Debit card",
        text: [
          "The Web.3 Neobanking Platform’s integrated debit card system allows users to spend cryptocurrency in real-life situations and enjoy the convenience and acceptance of traditional payment networks.",
        ],
        image: `${imgBasePath}/debit-card.png`,
      },
      {
        title: "Minimal transaction fee",
        text: [
          "One of the Web.3 Neobanking Platform’s main priorities is the convenience of users and low commissions for transactions with the help of protection technologies such as encryption, multi-factor authentication, and cold storage.",
          "The daily audits and penetration testing promote high-security standards. The Web.3 Neobanking Platform holds its PCI DSS Level 1 compliance, which proves the company's reliability in protecting and securing financial information.",
        ],
        image: `${imgBasePath}/minimal-transaction-fee.png`,
      },
    ],
  },

  statistics: {
    title: "Our professional collaboration",
    imageUrl: `${imgBasePath}/blocks-cooperation.png`,
    teams: [
      {
        title: "Agilie’s team",
        points: [
          "Team lead - 1",
          "PM - 1",
          "DevOps - 1",
          "Full stack - 1",
          "Backend developers - 2",
          "Frontend developers - 2",
          "QA - 1",
          "iOS developer - 1",
          "Android developer - 1",
          "Lead designer - 1",
          "Designer - 1",
          "Business analyst - 1",
        ],
      },
      {
        title: "Client’s team",
        points: ["CEO", "COO", "Product owner", "Head of the support team"],
      },
    ],
    points: [
      {
        mainPoint: "Dedicated team",
        secondPoint: "Service model",
      },
      {
        mainPoint: "18",
        secondPoint: "Mobile apps (iOS & Android)",
      },
      {
        mainPoint: "8",
        secondPoint: "Web platforms",
      },
      {
        mainPoint: "10k+",
        secondPoint: "Users on all platforms",
      },
    ],
  },
      feedback: {
        title: 'Client’s feedback',
        quoteSrc: '/mad-id-images/icons/quotes.png',
        comment: 'It was a pleasure to collaborate with Agilie. By prioritizing results and quality as their highest priorities, the developers are passionate about what they’re doing by creating truly remarkable solutions and bringing sophisticated customer experience.',
        authorName: 'Founder of Web.3 Neobanking Platform',
        authorAvatar: `${imgBasePath}/feedback.png`,
        authorSpecialist: ''
    },
};
